import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, PlusCircle, Clock, LayoutDashboard, LogOut, Menu, X, Bell, Rss, MessageSquare, Share2, Database, Coins, Handshake, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { SessionTimer } from '@/components/wellbeing/SessionTimer';
import { TwinneeWordmark } from '@/components/TwinneeWordmark';

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const user = JSON.parse(localStorage.getItem('Twinnee_user') || '{}');

  useEffect(() => {
    const syncUserRole = async () => {
      const token = localStorage.getItem('storycraft_token');
      if (!token) return;
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const res = await fetch(`${backendUrl}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const me = await res.json();
          setStaffRole(me.role);
          const prev = JSON.parse(localStorage.getItem('storycraft_user') || '{}');
          localStorage.setItem('storycraft_user', JSON.stringify({ ...prev, ...me }));
          if (typeof me.credit_balance === 'number') setCreditBalance(me.credit_balance);
        }
      } catch {
        /* ignore */
      }
    };
    syncUserRole();
  }, []);

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const token = localStorage.getItem('storycraft_token');
        if (!token) return;

        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const response = await fetch(backendUrl + '/api/notifications', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data || []);
        }
      } catch (err) {
        // Ignore errors
      }
    };
    
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('storycraft_token');
    localStorage.removeItem('storycraft_user');
    navigate('/');
  };

  const [staffRole, setStaffRole] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('storycraft_user') || '{}')?.role;
    } catch {
      return undefined;
    }
  });
  const [creditBalance, setCreditBalance] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('storycraft_user') || '{}');
      return typeof u.credit_balance === 'number' ? u.credit_balance : null;
    } catch {
      return null;
    }
  });
  const showAdmin = staffRole === 'admin' || staffRole === 'moderator';

  const links = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/channel', label: 'YouTube Analytics', icon: BarChart3 },
    ...(showAdmin ? [{ path: '/admin', label: 'Admin', icon: Database }] : []),
    { path: '/partners/creative', label: 'CPP', icon: Handshake },
    { path: '/social-publish', label: 'Publish', icon: Share2 },
    { path: '/flows', label: 'New Story', icon: PlusCircle },
    { path: '/tasks', label: 'Tasks', icon: Clock },
    { path: '/pricing', label: 'Pricing', icon: Coins },
  ];

  const isActive = (path) => (
    path.startsWith('/channel')
      ? location.pathname.startsWith('/channel') && !location.pathname.startsWith('/channel/trends')
      : location.pathname === path
  );

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 200, damping: 30 }}
      className="fixed top-0 left-0 right-0 z-50 glass-panel"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center" data-testid="nav-logo" title="Home">
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.1 }}
            >
              <TwinneeWordmark className="h-9 twinnee-wordmark--themed" data-testid="nav-logo-icon" />
            </motion.div>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {links.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200"
                style={{
                  background: isActive(path) ? 'hsl(var(--primary))' : 'transparent',
                  color: isActive(path) ? 'hsl(var(--primary-foreground))' : 'hsl(var(--muted-foreground))',
                  boxShadow: isActive(path) ? '0 4px 16px rgba(var(--shadow-color), 0.28)' : 'none',
                }}
                onMouseEnter={e => {
                  if (!isActive(path)) {
                    e.currentTarget.style.color = 'hsl(var(--foreground))';
                    e.currentTarget.style.background = 'hsl(var(--muted))';
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive(path)) {
                    e.currentTarget.style.color = 'hsl(var(--muted-foreground))';
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            {/* Session Timer */}
            <SessionTimer />
            
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                data-testid="notification-bell"
                className="relative p-2 rounded-lg transition-colors"
                style={{ 
                  background: showNotifications ? 'var(--primary-light)' : 'transparent',
                  color: 'var(--text-primary)' 
                }}
              >
                <Bell className="w-5 h-5" />
                {notifications.filter((n) => !n.read).length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: '#ef4444', color: 'white' }}>
                    {notifications.filter((n) => !n.read).length}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-80 glass-panel rounded-xl shadow-lg overflow-hidden z-50"
                    style={{ border: '1px solid var(--glass-border)' }}
                  >
                    <div className="p-3 border-b" style={{ borderColor: 'var(--glass-border)' }}>
                      <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Notifications</h3>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-4 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                          No new notifications
                        </div>
                      ) : (
                        notifications.map(notif => (
                          <button
                            key={notif.id}
                            type="button"
                            className="w-full text-left p-3 border-b hover:bg-opacity-50 transition-colors"
                            style={{
                              borderColor: 'var(--glass-border)',
                              background: notif.read ? 'transparent' : 'var(--primary-light)',
                            }}
                            onClick={() => {
                              setShowNotifications(false);
                              const target = notif.link || '/tasks';
                              if (target.startsWith('http')) {
                                window.location.href = target;
                              } else {
                                navigate(target.startsWith('/') ? target : `/tasks?job=${notif.job_id || ''}`);
                              }
                              if (notif.id && !notif.read) {
                                const token = localStorage.getItem('storycraft_token');
                                const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
                                fetch(`${backendUrl}/api/notifications/${notif.id}/read`, {
                                  method: 'PATCH',
                                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                                }).catch(() => {});
                              }
                            }}
                            data-testid={`notification-item-${notif.id}`}
                          >
                            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                              {notif.title}
                            </p>
                            <p className="text-xs mt-1 line-clamp-3" style={{ color: 'var(--text-secondary)' }}>
                              {notif.message}
                            </p>
                            {notif.result_url ? (
                              <p className="text-[10px] mt-1 truncate opacity-70" style={{ color: 'var(--text-tertiary)' }}>
                                Video ready
                              </p>
                            ) : null}
                          </button>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Theme Switcher */}
            <ThemeSwitcher />

            {creditBalance != null && (
              <Link
                to="/pricing"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors"
                style={{
                  background: 'var(--primary-light)',
                  color: 'hsl(var(--primary))',
                }}
                data-testid="nav-credit-balance"
                title="Your credit balance"
              >
                <Coins className="w-4 h-4" />
                {creditBalance.toFixed(0)}
              </Link>
            )}

            <span className="text-sm font-medium" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {user.name || 'User'}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="nav-logout-btn"
              className="rounded-full hover:text-red-500 transition-colors"
              style={{ color: 'hsl(var(--muted-foreground))' }}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <ThemeSwitcher />
            <button
              className="p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="nav-mobile-toggle"
              style={{ color: 'hsl(var(--foreground))' }}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden glass-panel border-t"
          style={{ borderColor: 'var(--glass-border)' }}
        >
          <div className="px-4 py-3 space-y-1">
            {links.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all"
                style={{
                  background: isActive(path) ? 'hsl(var(--primary))' : 'transparent',
                  color: isActive(path) ? 'hsl(var(--primary-foreground))' : 'hsl(var(--muted-foreground))',
                }}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            ))}
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-500 w-full"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};
